import React from 'react';
import { Button, Modal, ModalProps, Alert, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik, useFormikContext } from 'formik';
import { object, string, date, number } from 'yup';
import { map } from 'lodash';
import { TrixEditor } from 'react-trix';
import { useSelector } from 'react-redux';

import { formatDate } from '@utils/datetime';
import { FormGroupControl, FormGroupSelect, RowItem, RowItemProps } from '@components/Base';
import { InsurancePolicy } from '@models/InsurancePolicy';
import { InsurancePolicyUserList } from '@models/InsurancePolicyUser';
import { noob } from '@utils/typescript';
import { mergeInitialValues } from '@utils/formik';
import { ServerError } from '@components/Helper';

import { selectFeatureInsurancePolicyUser } from './selector';
import { InsurancePolicyUserCreatePayload } from './action';
import { tGender, UserLess } from '@models/User';

export type InsurancePolicyUserFormValues = Omit<InsurancePolicyUserCreatePayload, 'userId' | 'insurancePolicyId'>;

const GROUP_PROPS = { className: 'mb-3' };
const ROW_ITEM_PROPS: RowItemProps = {
  className: 'mb-2',
  labelProps: { xs: 'auto', sm: 'auto', md: 'auto' },
};

type FormFormProps = Pick<ModalProps, 'onHide'> & Pick<InsurancePolicy, 'policyBenefitTemplates'> & { user: UserLess | null };

const FormForm = ({ onHide, policyBenefitTemplates, user }: FormFormProps) => {
  const { t } = useTranslation();
  const { serverErrors } = useSelector(selectFeatureInsurancePolicyUser);
  const { values, setFieldValue, errors } = useFormikContext<InsurancePolicyUserFormValues>();

  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>Member Policy</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="secondary">
          <p>User Info</p>
          <Row>
            <Col xs={6} md={4}><RowItem {...ROW_ITEM_PROPS} label={t('user.name')} value={user?.name} /></Col>
            <Col xs={6} md={4}><RowItem {...ROW_ITEM_PROPS} label={t('user.dob')} value={formatDate(user?.dob)} /></Col>
            <Col xs={6} md={4}><RowItem {...ROW_ITEM_PROPS} label={t('user.gender')} value={tGender(user?.gender)} /></Col>
            <Col xs={6} md={4}><RowItem {...ROW_ITEM_PROPS} label={t('user.mobileNumber')} value={user?.mobileNumber} /></Col>
            <Col xs={6} md={4}><RowItem {...ROW_ITEM_PROPS} label={t('user.email')} value={user?.email} /></Col>
            <Col xs={6} md={4}><RowItem {...ROW_ITEM_PROPS} label={t('user.occupation')} value={user?.occupation} /></Col>
            <Col xs={12}><RowItem {...ROW_ITEM_PROPS} label={t('user.address')} value={user?.address} /></Col>
          </Row>
        </Alert>

        <FormGroupSelect
          asRow
          name="policyBenefitTemplateId"
          label="Coverage"
          groupProps={GROUP_PROPS}
          required
        >
          <option></option>
          {map(policyBenefitTemplates, ({ id, name }) => (
            <option key={name} value={id}>
              {name}
            </option>
          ))}
        </FormGroupSelect>
        <FormGroupControl
          asRow
          name="insurerMemberId"
          label="Insurer Member ID"
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="effectiveDate"
          label={t('common:effectiveDate')}
          type="date"
          required
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="expirationDate"
          label={t('common:expirationDate')}
          type="date"
          required
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="premium"
          label={t('common:premium')}
          type="number"
          required
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="refundedPremium"
          label={t('common:refundedPremium')}
          type="number"
          groupProps={GROUP_PROPS}
        />

        <div className="mb-3">
          <label className={errors?.descriptionHtml ? 'is-invalid' : ''}>{t('common:fillDescriptionDetails')}*</label>
          {errors?.descriptionHtml && (
            <div className="invalid-feedback">{errors.descriptionHtml}</div>
          )}
        </div>
        <TrixEditor
          className="trix-content"
          onChange={(html) => setFieldValue('descriptionHtml', html)}
          onEditorReady={noob}
          mergeTags={[]}
          value={values.descriptionHtml}
          placeholder={t('common:description')}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:save')}
        </Button>
      </Modal.Footer>
      <ServerError errors={serverErrors} />
    </Form >
  );
}

type Props = ModalProps & Pick<InsurancePolicy, 'effectiveDate' | 'expirationDate' | 'policyBenefitTemplates'> & {
  onSubmit: (values: InsurancePolicyUserFormValues) => void;
  policyUser: InsurancePolicyUserList | null;
  user: UserLess | null;
}

const InsurancePolicyUserFormModal = ({ onSubmit, policyBenefitTemplates, effectiveDate, expirationDate, policyUser, user, ...props }: Props) => {
  const { t } = useTranslation();
  const defaultValue: InsurancePolicyUserFormValues = {
    policyBenefitTemplateId: '',
    insurerMemberId: '',
    effectiveDate: '',
    expirationDate: '',
    premium: '',
    refundedPremium: 0.0,
    descriptionHtml: '',
  };
  const initialValues = mergeInitialValues(defaultValue, { effectiveDate, expirationDate }, policyUser);

  const validationSchema = object({
    policyBenefitTemplateId: number().required(t('validation:required')),
    insurerMemberId: string().optional(),
    effectiveDate: date().required(t('validation:required')),
    expirationDate: date().required(t('validation:required')),
    premium: number().required(t('validation:required')),
    refundedPremium: number().optional(),
    descriptionHtml: string().required(t('validation:required')),
  });

  return (
    <Modal {...props} size="xl">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <FormForm onHide={props.onHide} policyBenefitTemplates={policyBenefitTemplates} user={user} />
      </Formik>
    </Modal>
  )
};

export { InsurancePolicyUserFormModal };
